import {
  answerDigest,
  helpPhrase,
  isAuthClose,
  normalizeAnswer,
  parseClientMessage,
  type ClientMessage,
  type ServerFrame,
} from "@tutor/protocol";
import { initialView, reduce, type LocalAction, type ViewInput } from "./reducer";
import type {
  ItemView,
  LearnerClient,
  LearnerView,
  LiveAuth,
  LiveStorage,
  LocalVerdict,
  StoredItem,
  StoredState,
} from "./types";

/** WebSocket subprotocol of the live channel; the server selects it. */
export const LIVE_PROTOCOL = "tutor.v1";
/** The learner token travels as a subprotocol: browsers cannot set socket headers. */
const TOKEN_PROTOCOL_PREFIX = "auth.";

const BACKOFF_BASE_MS = 500;
const BACKOFF_CAP_MS = 15_000;
const SAVE_DEBOUNCE_MS = 100;
/** Longest delay a browser timer honours; a longer expiry timer re-arms itself. */
const MAX_TIMER_MS = 2_147_483_647;

/** The part of `WebSocket` the client uses; the browser's `WebSocket` satisfies it. */
export interface LiveSocket {
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type LiveSocketConstructor = new (url: string, protocols: string[]) => LiveSocket;

/** Connectivity as the browser reports it (`navigator.onLine` and its events). */
export interface NetworkSignal {
  isOnline(): boolean;
  /** Calls `listener` on every change. Returns an unsubscribe function. */
  subscribe(listener: (online: boolean) => void): () => void;
}

export interface LearnerClientOptions {
  readonly auth: LiveAuth;
  /** The live URL for a resume after `since`, e.g. a same-origin `wss://…/live?since=`. */
  url(profileId: string, since: number): string;
  readonly storage: LiveStorage;
  /** Defaults to the global `WebSocket`. */
  readonly WebSocket?: LiveSocketConstructor;
  /** Defaults to the browser's `online`/`offline` events; always online without them. */
  readonly network?: NetworkSignal;
  /** Jitter source in [0, 1). Defaults to `Math.random`. */
  readonly random?: () => number;
  /** Schedules `callback`, returning a cancel function. Defaults to the global timer. */
  readonly setTimeout?: (callback: () => void, ms: number) => () => void;
  /** Milliseconds since the epoch, for token expiry. Defaults to `Date.now`. */
  readonly now?: () => number;
  /** `clientMsgId` source. Defaults to `crypto.randomUUID`. */
  readonly newId?: () => string;
}

interface OutboxEntry {
  readonly id: string;
  readonly raw: string;
}

/**
 * Opens the live channel for one learner: renders from storage at once, resumes the log
 * with `?since=`, queues client frames in a persistent outbox and checks closed answers
 * locally. Nothing here logs: frames carry a child's words and answers.
 */
export function createLearnerClient(options: LearnerClientOptions): LearnerClient {
  const Socket: LiveSocketConstructor = options.WebSocket ?? globalThis.WebSocket;
  const network = options.network ?? browserNetwork();
  const random = options.random ?? Math.random;
  const schedule = options.setTimeout ?? defaultSetTimeout;
  const now = options.now ?? Date.now;
  const newId = options.newId ?? (() => crypto.randomUUID());
  const { storage } = options;
  const profileId = options.auth.profileId;

  let auth = options.auth;
  let view = initialView();
  let outbox: OutboxEntry[] = [];
  const listeners = new Set<(view: LearnerView) => void>();

  // Local actions taken before storage answered, replayed onto the stored view.
  let loaded = false;
  const earlyActions: LocalAction[] = [];

  let stopped = false;
  let socket: LiveSocket | null = null;
  let attempt = 0;
  // The headSeq a gap reconnect resumed from: a second gap there means the actor no
  // longer has those events, so the client accepts the gap instead of looping.
  let gapRetriedAt: number | null = null;
  let cancelRetry: (() => void) | null = null;
  let cancelExpiry: (() => void) | null = null;
  let cancelSave: (() => void) | null = null;
  let saving: Promise<void> = Promise.resolve();

  function commit(next: LearnerView): void {
    const withQueue = next.queued === outbox.length ? next : { ...next, queued: outbox.length };
    if (withQueue === view) return;
    view = withQueue;
    scheduleSave();
    for (const listener of listeners) listener(view);
  }

  function apply(input: ViewInput): void {
    commit(reduce(view, input));
  }

  function setStatus(status: LearnerView["status"]): void {
    if (view.status !== status) commit({ ...view, status });
  }

  function local(action: LocalAction): void {
    if (!loaded) earlyActions.push(action);
    apply(action);
  }

  // --- persistence -------------------------------------------------------------------

  function scheduleSave(): void {
    if (!loaded || cancelSave !== null) return;
    cancelSave = schedule(saveNow, SAVE_DEBOUNCE_MS);
  }

  function saveNow(): void {
    cancelSave = null;
    const {
      headSeq,
      messages,
      item,
      upNext,
      layout,
      mode,
      skills,
      progress,
      moment,
      curiosity,
      oneBoard,
      help,
      workbench,
      practice,
      plan,
      opener,
      ended,
      climbs,
      pages,
      class: classView,
    } = view;
    const state: StoredState = {
      view: {
        headSeq,
        messages,
        item,
        upNext,
        layout,
        mode,
        skills,
        progress,
        moment,
        curiosity,
        oneBoard,
        help,
        workbench,
        practice,
        plan,
        opener,
        ended,
        climbs,
        pages,
        class: classView,
      },
      outbox: outbox.map((e) => e.raw),
    };
    // Chained so an older snapshot never lands after a newer one. A failed save is
    // retried by the next change; there is nothing useful (or private) to log.
    saving = saving.then(() => storage.save(profileId, state)).catch(() => undefined);
  }

  async function start(): Promise<void> {
    let stored: StoredState | null;
    try {
      stored = await storage.load(profileId);
    } catch {
      stored = null;
    }
    if (stored !== null) {
      const restored: OutboxEntry[] = [];
      for (const raw of stored.outbox) {
        const parsed = parseClientMessage(raw);
        if (parsed.ok) restored.push({ id: parsed.message.clientMsgId, raw });
      }
      // Frames queued while loading go after the stored ones, keeping send order.
      outbox = [...restored, ...outbox];
      let next: LearnerView = {
        ...stored.view,
        item: restoreItem(stored.view.item),
        upNext: restoreItem(stored.view.upNext),
        mode: stored.view.mode ?? "text",
        // Snapshots saved before ADR-0040 have no progress yet.
        progress: stored.view.progress ?? {},
        moment: stored.view.moment ?? null,
        curiosity: stored.view.curiosity ?? true,
        oneBoard: stored.view.oneBoard ?? false,
        help: stored.view.help ?? null,
        workbench: stored.view.workbench ?? null,
        practice: stored.view.practice ?? null,
        plan: stored.view.plan ?? null,
        opener: stored.view.opener ?? null,
        ended: stored.view.ended ?? null,
        climbs: stored.view.climbs ?? {},
        pages: (stored.view.pages ?? []).flatMap((p) => restoreItem(p) ?? []),
        class: stored.view.class ?? null,
        status: view.status,
        queued: outbox.length,
      };
      for (const action of earlyActions) next = reduce(next, action);
      view = next;
    }
    earlyActions.length = 0;
    loaded = true;
    for (const listener of listeners) listener(view);
    if (stopped) return;
    scheduleSave();
    scheduleExpiry();
    connect();
  }

  // --- connection ----------------------------------------------------------------------

  function tokenExpired(): boolean {
    const expiry = tokenExpiry(auth.token);
    return expiry !== null && now() >= expiry;
  }

  function connect(): void {
    cancelRetry?.();
    cancelRetry = null;
    if (stopped || !loaded || socket !== null) return;
    // The Worker refuses a bad token before the upgrade, which a browser only sees as an
    // anonymous close; the expiry claim is the one signal the client can read itself.
    if (tokenExpired()) {
      setStatus("unauthorized");
      return;
    }
    if (!network.isOnline()) {
      setStatus("offline");
      return;
    }
    setStatus("connecting");
    let ws: LiveSocket;
    try {
      ws = new Socket(options.url(profileId, view.headSeq), [
        LIVE_PROTOCOL,
        TOKEN_PROTOCOL_PREFIX + auth.token,
      ]);
    } catch {
      retryLater();
      return;
    }
    socket = ws;
    ws.onmessage = (event) => {
      if (socket === ws && typeof event.data === "string") receive(ws, event.data);
    };
    ws.onclose = (event) => {
      if (socket !== ws) return;
      socket = null;
      // The actor closes with 4001 (token expired) or 4003 (device revoked, PIN reset):
      // reconnecting with the same token would not help, so ask for a new one.
      if (isAuthClose(event.code)) {
        cancelRetry?.();
        cancelRetry = null;
        setStatus("unauthorized");
        return;
      }
      retryLater();
    };
    // An error is always followed by a close, which does the work.
    ws.onerror = () => undefined;
  }

  function retryLater(): void {
    if (stopped) return;
    if (tokenExpired()) {
      setStatus("unauthorized");
      return;
    }
    setStatus("offline");
    // No reconnect spin while the device knows it is offline; `online` resumes.
    if (!network.isOnline()) return;
    const ceiling = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt);
    attempt += 1;
    cancelRetry?.();
    cancelRetry = schedule(connect, ceiling / 2 + (random() * ceiling) / 2);
  }

  function dropSocket(): void {
    const ws = socket;
    socket = null;
    if (ws === null) return;
    try {
      ws.close(1000);
    } catch {
      // Already closing.
    }
  }

  function receive(ws: LiveSocket, raw: string): void {
    let frame: unknown;
    try {
      frame = JSON.parse(raw);
    } catch {
      return;
    }
    if (!isServerFrame(frame)) return;
    switch (frame.type) {
      case "event":
        if (frame.seq > view.headSeq + 1 && gapRetriedAt !== view.headSeq) {
          gapRetriedAt = view.headSeq;
          dropSocket();
          connect();
          return;
        }
        apply(frame);
        return;
      case "ready":
        attempt = 0;
        gapRetriedAt = null;
        apply(frame);
        // The actor de-duplicates by clientMsgId, so resending what it already has is safe.
        for (const entry of outbox) send(ws, entry.raw);
        return;
      case "ack": {
        const acked = outbox.find((e) => e.id === frame.clientMsgId);
        if (acked !== undefined) {
          outbox = outbox.filter((e) => e !== acked);
          // A kept help tap or brouillon line was published before its ack; one still
          // pending was refused (or, for a line, sent to the conversation instead).
          const parsed = parseClientMessage(acked.raw);
          if (
            parsed.ok &&
            (parsed.message.type === "help.request" ||
              parsed.message.type === "work.write" ||
              parsed.message.type === "teachback.submit")
          ) {
            apply({ type: "local.unsent", clientMsgId: frame.clientMsgId });
          } else {
            commit(view);
          }
        }
        return;
      }
      case "stream":
      case "error":
        apply(frame);
        return;
    }
  }

  function send(ws: LiveSocket, raw: string): void {
    try {
      ws.send(raw);
    } catch {
      // The frame stays in the outbox and goes again after the next `ready`.
    }
  }

  /** Queues a frame and its optimistic change together, or neither. */
  function enqueue(message: ClientMessage, action?: LocalAction): boolean {
    const raw = JSON.stringify(message);
    // A frame the actor would reject would sit in the outbox forever.
    if (!parseClientMessage(raw).ok) return false;
    outbox = [...outbox, { id: message.clientMsgId, raw }];
    if (action === undefined) commit(view);
    else local(action);
    if (socket !== null && view.status === "live") send(socket, raw);
    return true;
  }

  function scheduleExpiry(): void {
    cancelExpiry?.();
    cancelExpiry = null;
    const expiry = tokenExpiry(auth.token);
    if (expiry === null || stopped) return;
    const delay = expiry - now();
    if (delay <= 0) return;
    cancelExpiry = schedule(
      () => {
        cancelExpiry = null;
        if (!tokenExpired()) {
          scheduleExpiry();
          return;
        }
        cancelRetry?.();
        cancelRetry = null;
        dropSocket();
        setStatus("unauthorized");
      },
      Math.min(delay, MAX_TIMER_MS),
    );
  }

  const unsubscribeNetwork = network.subscribe((online) => {
    if (stopped || !loaded || socket !== null || view.status === "unauthorized") return;
    if (online) {
      attempt = 0;
      connect();
    } else {
      cancelRetry?.();
      cancelRetry = null;
      setStatus("offline");
    }
  });

  void start();

  return {
    getView: () => view,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    say(text, channel) {
      const clientMsgId = newId();
      // The exercise tab sends no channel, exactly as before free questions (ADR-0042).
      const tab = channel === "curiosity" ? { channel } : {};
      enqueue(
        { type: "learner.say", clientMsgId, text, ...tab },
        { type: "local.say", clientMsgId, text, ...tab },
      );
    },
    async answer(itemId, answer) {
      const item = view.item?.itemId === itemId ? view.item : null;
      const verdict = item === null ? "unknown" : await localVerdict(item, answer);
      const clientMsgId = newId();
      enqueue(
        { type: "answer.submit", clientMsgId, itemId, answer },
        { type: "local.attempt", clientMsgId, itemId, answer, local: verdict },
      );
      return verdict;
    },
    requestItem(locale) {
      enqueue({ type: "item.request", clientMsgId: newId(), locale, opener: true });
    },
    pickSession(sessionStart, skillId, locale) {
      enqueue({ type: "session.pick", clientMsgId: newId(), sessionStart, skillId, locale });
    },
    endSession(sessionStart, mood) {
      enqueue(
        { type: "session.end", clientMsgId: newId(), sessionStart, mood },
        { type: "local.end", sessionStart, mood },
      );
    },
    requestHelp(itemId, kind, locale) {
      const clientMsgId = newId();
      enqueue(
        { type: "help.request", clientMsgId, itemId, kind },
        { type: "local.say", clientMsgId, text: helpPhrase(kind, locale) },
      );
    },
    advance() {
      const next = view.upNext;
      if (next === null) return;
      // The actor briefs Coach on the item on screen, so it has to hear about the move.
      enqueue(
        { type: "item.advance", clientMsgId: newId(), itemId: next.itemId },
        { type: "local.advance" },
      );
    },
    write(itemId, text) {
      const line = text.trim();
      if (line === "") return;
      const clientMsgId = newId();
      enqueue(
        { type: "work.write", clientMsgId, itemId, text: line },
        { type: "local.write", clientMsgId, itemId, text: line },
      );
    },
    explain(itemId, text) {
      const explanation = text.trim();
      if (explanation === "") return;
      const clientMsgId = newId();
      enqueue(
        { type: "teachback.submit", clientMsgId, itemId, text: explanation },
        { type: "local.explain", clientMsgId, itemId, text: explanation },
      );
    },
    checkLesson(itemId, answer) {
      const value = answer.trim();
      if (value === "") return;
      enqueue({ type: "lesson.answer", clientMsgId: newId(), itemId, answer: value });
    },
    skipLesson(itemId) {
      enqueue(
        { type: "lesson.skip", clientMsgId: newId(), itemId },
        { type: "local.skipLesson", itemId },
      );
    },
    pickChapter(section) {
      enqueue(
        { type: "class.pick", clientMsgId: newId(), section },
        { type: "local.classPick", section },
      );
    },
    dismissMoment() {
      local({ type: "local.dismissMoment" });
    },
    reauthenticate(next) {
      if (next.profileId !== profileId) {
        throw new Error("reauthenticate: use a new client for another profile");
      }
      auth = next;
      if (stopped) return;
      cancelRetry?.();
      cancelRetry = null;
      dropSocket();
      attempt = 0;
      scheduleExpiry();
      connect();
    },
    close() {
      if (stopped) return;
      stopped = true;
      unsubscribeNetwork();
      cancelRetry?.();
      cancelExpiry?.();
      cancelRetry = null;
      cancelExpiry = null;
      dropSocket();
      setStatus("offline");
      if (loaded) {
        cancelSave?.();
        saveNow();
      }
    },
  };
}

/** An item from a snapshot, with what older snapshots lack. */
function restoreItem(item: StoredItem | null): ItemView | null {
  return item === null
    ? null
    : {
        ...item,
        moment: item.moment ?? "try",
        lines: item.lines ?? [],
        mark: item.mark ?? null,
        steps: item.steps ?? [],
      };
}

/** Compares the salted digest of the normalised answer; never throws. */
async function localVerdict(item: ItemView, answer: string): Promise<LocalVerdict> {
  if (item.check === null) return "unknown";
  const normalized = normalizeAnswer(item.answer.kind, answer);
  if (normalized === null) return "unknown";
  try {
    const digest = await answerDigest(item.check.salt, normalized);
    return digest === item.check.digest ? "correct" : "wrong";
  } catch {
    // No Web Crypto (insecure context): the server's grade still arrives.
    return "unknown";
  }
}

/**
 * The `exp` claim of a JWT in milliseconds, read without verification (the server
 * verifies); `null` when the token has no readable expiry.
 */
export function tokenExpiry(token: string): number | null {
  const payload = token.split(".")[1];
  if (payload === undefined) return null;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims: unknown = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")));
    if (typeof claims !== "object" || claims === null || !("exp" in claims)) return null;
    return typeof claims.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

function defaultSetTimeout(callback: () => void, ms: number): () => void {
  const handle = globalThis.setTimeout(callback, ms);
  return () => {
    globalThis.clearTimeout(handle);
  };
}

function browserNetwork(): NetworkSignal {
  // Typed as optional: Node and some embedded webviews have neither.
  const nav = optionalNavigator();
  const target: {
    addEventListener?: (type: string, listener: () => void) => void;
    removeEventListener?: (type: string, listener: () => void) => void;
  } = globalThis;
  return {
    isOnline: () => nav?.onLine !== false,
    subscribe(listener) {
      const online = () => {
        listener(true);
      };
      const offline = () => {
        listener(false);
      };
      target.addEventListener?.("online", online);
      target.addEventListener?.("offline", offline);
      return () => {
        target.removeEventListener?.("online", online);
        target.removeEventListener?.("offline", offline);
      };
    },
  };
}

function optionalNavigator(): { readonly onLine?: boolean } | undefined {
  return globalThis.navigator;
}

const FRAME_TYPES = new Set(["event", "ready", "stream", "ack", "error"]);

// Shape check only: frames come from our own actor, same origin. It keeps a proxy's
// HTML error page or a future frame type from reaching the reducer.
function isServerFrame(value: unknown): value is ServerFrame {
  if (typeof value !== "object" || value === null || !("type" in value)) return false;
  if (typeof value.type !== "string" || !FRAME_TYPES.has(value.type)) return false;
  switch (value.type) {
    case "event":
      return (
        "seq" in value &&
        typeof value.seq === "number" &&
        "event" in value &&
        typeof value.event === "object" &&
        value.event !== null
      );
    case "ready":
      return "headSeq" in value && typeof value.headSeq === "number";
    case "stream":
      return (
        "offset" in value &&
        typeof value.offset === "number" &&
        "text" in value &&
        typeof value.text === "string"
      );
    case "ack":
      return "clientMsgId" in value && typeof value.clientMsgId === "string";
    default:
      return true;
  }
}
